import { GetStaticProps } from 'next';

import { BoxList } from '@components/BoxList';
import { HeroHeadline, HeroHeadlineProps } from '@components/HeroHeadline';
import { ImageSection, ImageSectionProps } from '@components/ImageSection';
import { ListWithImage, ListWithImageProps } from '@components/ListWithImage';
import { LogoLinks } from '@components/LogoLinks';
import {
	ProductShowcase,
	ProductShowcaseProps,
} from '@components/ProductShowcase';
import { QuoteSection, QuoteSectionProps } from '@components/QuoteSection';
import {
	SellFormSection,
	SellFormSectionProps,
} from '@components/SellFormSection';
import { SellPage as SellPageWrapper } from '@components/SellPage';

import { getSellPage } from '@data/sanity/queries/sell-page';
import { CmsBoxList, CmsLinkWithImage, CmsSellPage } from '@interfaces/common';
import { neverError } from '@utils/neverError';
import { withSiteWideData } from '@utils/page';

interface SellPageProps {
	sellPage: CmsSellPage;
}

const SellPage: React.VFC<SellPageProps> = ({ sellPage }) => {
	const { sections } = sellPage;
	let isFirstLevelHeadingSet = false;

	return (
		<SellPageWrapper>
			{sections?.map(({ _type, _key, ...rest }) => {
				switch (_type) {
					case 'heroHeadline': {
						const headingLevel = !isFirstLevelHeadingSet ? 1 : 2;

						isFirstLevelHeadingSet = true;
						return (
							<HeroHeadline
								headingLevel={headingLevel}
								key={_key}
								{...(rest as HeroHeadlineProps)}
							/>
						);
					}
					case 'imageSection': {
						return <ImageSection key={_key} {...(rest as ImageSectionProps)} />;
					}
					case 'listWithImage': {
						return (
							<ListWithImage key={_key} {...(rest as ListWithImageProps)} />
						);
					}
					case 'sellEnquiryForm': {
						return (
							<SellFormSection key={_key} {...(rest as SellFormSectionProps)} />
						);
					}
					case 'productShowcase': {
						return (
							<ProductShowcase key={_key} {...(rest as ProductShowcaseProps)} />
						);
					}
					case 'resourceList': {
						return <BoxList key={_key} {...(rest as CmsBoxList)} />;
					}
					case 'quoteSection': {
						return <QuoteSection key={_key} {...(rest as QuoteSectionProps)} />;
					}
					case 'storeLinksSection': {
						return (
							<LogoLinks
								key={_key}
								layout="grid"
								{...(rest as { links: CmsLinkWithImage[] })}
							/>
						);
					}
					case undefined:
						// Newly added section types come back as undefined
						// We don’t want to display an error in this case
						return null;
					default: {
						throw neverError('Unknown section type', _type);
					}
				}
			})}
		</SellPageWrapper>
	);
};

export const getStaticProps: GetStaticProps = withSiteWideData<SellPageProps>(
	async ({ locale }) => {
		const sellPage = await getSellPage(locale);

		if (!sellPage) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				metadata: sellPage.metadata || null,
				hasNewsLetterSection: false,
				sellPage,
			},
		};
	}
);

export default SellPage;
